<template>
  <div class="page-builder-wrapper">
    <div class="modal task">
      <div class="modal-close" @click="close"></div>
      <div class="custom-modal-header">page</div>
      <div class="content">
        <div class="custom-page-wrapper" v-if="!loading">
          <div class="report-tools col-4">
            <div
              class="round-button item"
              @click="exportPDF()"
              v-b-tooltip.hover.top
              title="PDF Herunterladen"
            >
              <i class="fas fa-file-pdf"></i>
            </div>
            <div
              class="round-button item"
              @click="exportHTML()"
              v-b-tooltip.hover.top
              title="HTML Herunterladen"
            >
              <i class="fas fa-file-invoice"></i>
            </div>

            <div
              class="round-button item"
              @click="showPagePreview()"
              v-b-tooltip.hover.top
              title="Vorschau"
            >
              <img :src="require('@/assets/img/eye-icon.svg')" />
            </div>
            <div
              class="round-button item"
              :class="{ disabled: !isUserHasAccess }"
              @click="saveFlow()"
              v-b-tooltip.hover.top
              title="Speichern"
            >
              <i class="fa fa-save"></i>
            </div>
            <div
              class="round-button item"
              title="settings"
              v-b-tooltip.hover.top
              @click="toggleSetting(true)"
            >
              <i class="fas fa-cog"></i>
            </div>
          </div>
          <div class="custom-page-header col-4">
            <b-form-input
              v-model.trim="task.props.reportName"
              placeholder="Dateinamen"
              :class="{ 'is-duplicate': isNameDuplicate, error: shakeName }"
            ></b-form-input>
          </div>
        </div>
        <div class="settings-modal-wrapper" v-if="isSettingsModalVisible">
          <PagebuilderSettingsModal
            :task="task"
            @onClose="toggleSetting(false)"
            :isUserHasAccess="isUserHasAccess"
          />
        </div>
        <div
          v-if="!loading"
          class="page-builder-outer-wrapper fancy-scroll"
          @scroll.passive="showScrollbar"
        >
          <PageBuilderComponent
            v-if="isUserHasAccess"
            ref="pageBuilderComponentRef"
            :task="task"
            :taskProps="task.props"
            :userTheme="process && process ? process.style : null"
            @onContentChange="onContentChange"
            :workflows="workflows"
            :getWorkFlowData="getWorkFlowData"
            :getCustomWidgets="onGetCustomWidgets"
            :saveCustomWidgets="onSaveCustomWidgets"
            :margin="task.props.margin"
          ></PageBuilderComponent>
        </div>

        <div class="loading-container" v-if="loading">
          <LoadingSpinner
            loadingCaption="Wird gespeichert..."
            v-show="loading"
          />
        </div>

        <!-- preview wrapper -->
        <div
          class="preview-wrapper"
          v-if="pagePreviewBlobURL || isPreviewLoading"
        >
          <div class="loading-container" v-if="isPreviewLoading">
            <LoadingSpinner
              loadingCaption="Vorschau wird geladen..."
              v-show="isPreviewLoading"
            />
          </div>
          <template v-if="!isPreviewLoading">
            <PagePreviewModal
              @onClose="toggleOffPreview"
              :url="pagePreviewBlobURL"
            />
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { PageBuilderComponent } from '@codelos.io/page-builder';
import PagebuilderSettingsModal from '@/components/PageBuilderSettingsModal.vue';
import '@codelos.io/page-builder/dist/page-builder.css';
import LoadingSpinner from '../widgets/LoadingSpinner.vue';
import { generatePdf, getWorkFlowById } from '../../services/workflow.service';
import { mapGetters } from 'vuex';
import { debouncer } from '../../utils/helpers';

import PagePreviewModal from '@/components/modals/PagePreviewModal.vue';

import {
  saveCustomWidgets,
  getCustomWidgets,
  pageChartTypes,
  getPagePropChartKey,
} from '@/utils/helpers/PageBuilderHelper.js';
export default {
  name: 'pageBuilderWarp',
  data() {
    return {
      outputContent: null,
      loading: false,
      shakeName: false,
      isSettingsModalVisible: false,
      isPreviewLoading: false,
      pagePreviewBlobURL: '',
    };
  },

  props: {
    task: Object,
    process: { type: Object, required: true },
    getFileNamesFromProps: {
      type: Function,
      required: true,
      default: () => {},
    },
    isUserHasAccess: {
      type: Boolean,
      required: false,
      default: true,
    },
  },

  created() {
    // for backward comparability of report name
    if (!this.task.props.reportName) {
      this.task.props.reportName = `file-${new Date().getTime().toString()}`;
    }
    // for backward comparability of bold rows
    if (this.task.props.jsonContent) {
      this.task.props.jsonContent.children.forEach((pageNode) => {
        if (pageNode.type === 'table' && !('boldRows' in pageNode.value)) {
          pageNode.value['boldRows'] = new Array(pageNode.value.rows).fill(
            false
          );
        }
      });
    }
  },

  mounted() {
    this.task.props.jsonContent = this.$nextTick(() => {
      this.task.props.jsonContent =
        this.task.props.jsonContent ||
        this.$refs.pageBuilderComponentRef?.pageBuilderNodes;
      this.task.props.htmlContent =
        this.task.props.htmlContent ||
        this.$refs.pageBuilderComponentRef.getSanitizedString();
    });
    setTimeout(() => {
      // add chart data if not exist
      this.addChartDataInPageNode();
    });
  },
  beforeDestroy() {
    this.revokePageURL();
  },

  computed: {
    ...mapGetters({
      workflows: 'getAllFlows',
    }),
    isNameDuplicate() {
      const reportFilenames = this.getFileNamesFromProps(this.task.id);
      return (
        reportFilenames.includes(this.task.props.reportName) ||
        !this.task.props.reportName
      );
    },
    getAPIPayloadInputs() {
      return this.task.input.filter((taskInput) =>
        [
          'text',
          'number',
          'email',
          'radio',
          'timeseries',
          'formatted_number',
          'dataframe',
          'highchart',
          'select',
          'date',
          'datetime-local',
          'textarea',
          'range',
          'title',
          'model',
          'json',
          'JSON_object',
        ].includes(taskInput.type.toLowerCase())
      );
    },
  },
  methods: {
    async close() {
      if (!this.isNameDuplicate) {
        this.loading = true;
        await this.saveAsOutput();
        setTimeout(() => {
          this.loading = false;
          this.$emit('close');
        }, 1000);
      } else {
        this.shakeName = true;
        setTimeout(() => {
          this.shakeName = false;
        }, 500);
      }
    },

    onContentChange(event) {
      this.task.props.jsonContent = event.jsonContent;
      this.task.props.htmlContent = event.htmlContent;
    },

    async saveAsOutput() {
      const output = await this.getReport();
      this.task.output = [output];
      this.$set(this.task, 'output', this.task.output.concat(this.task.input));
    },

    async getReport() {
      const payload = {
        content: this.getPagePayloadContent(),
        values: this.getAPIPayloadInputs,
        filename: this.task.props.reportName,
      };

      try {
        const res = await generatePdf(payload);

        if (res.status != 200) throw new Error(res.statusText);

        return {
          label: `${payload.filename}.pdf`,
          name: `${payload.filename}.pdf`,
          type: 'file',
          value: res.data.data,
        };
      } catch (err) {
        return {
          label: 'error',
          type: 'text',
          value: err,
        };
      }
    },
    replacePlaceholder(name) {
      if (name.includes('{')) {
        let placeholder = name.split('{')[1].split('}')[0];
        let match = this.task.input.filter((e) => e.name == placeholder)[0]
          .value;
        name = name.replace('{' + placeholder + '}', match);
      }
      return name;
    },
    async exportPDF() {
      this.loadingMessage = 'PDF-Dokument wird erstellt...';
      this.loading = true;
      const report = await this.getReport();
      const linkSource = `data:application/pdf;base64,${report.value}`;
      const name = this.replacePlaceholder(this.task.props.reportName);
      const link = document.createElement('a');
      link.href = linkSource;
      link.download = name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      this.loading = false;
    },

    async exportHTML() {
      this.loading = true;
      try {
        const payload = {
          content: this.getPagePayloadContent(),
          values: this.getAPIPayloadInputs,
          filename: this.task.props.reportName,
          htmlOnly: true,
        };
        const htmlString = await generatePdf(payload);
        const name = this.replacePlaceholder(this.task.props.reportName);
        const blob = new Blob([htmlString.data.data], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${name}.html`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch (error) {
        this.$emit(
          'showToaster',
          'Nicht in der Lage, HTML zu generieren',
          'Fehler',
          'danger'
        );
      } finally {
        this.loading = false;
      }
    },
    async getWorkFlowData(workflowId) {
      try {
        const response = await getWorkFlowById(workflowId);
        return response.data;
      } catch (error) {
        //
      }
    },
    showScrollbar(event) {
      event.target.classList.add('show-scrollbar');
      debouncer(
        () => {
          event.target.classList.remove('show-scrollbar');
        },
        300,
        this,
        'removeScrollClass'
      );
    },

    async onGetCustomWidgets() {
      const widgets = await getCustomWidgets();
      return widgets;
    },

    getPagePayloadContent() {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = this.task.props.htmlContent;
      const htmlElements = tempDiv.querySelectorAll('.is-empty');
      htmlElements.forEach((element) => {
        element.classList.remove('is-empty');
      });
      return tempDiv.innerHTML;
    },

    async onSaveCustomWidgets(widgetElement) {
      try {
        await saveCustomWidgets(widgetElement);
      } catch (error) {
        throw new Error(error);
      }
    },
    resetPageChartData(pageChildren) {
      pageChildren.forEach((pageNode) => {
        if (pageChartTypes.includes(pageNode.type)) {
          pageNode.value[getPagePropChartKey(pageNode.type)] = {};
        }
        if (pageNode.type == 'col') {
          pageNode.elements.forEach((element) => {
            this.resetPageChartData(element.children);
          });
        }
      });
    },
    getFilterProcessData() {
      const process = JSON.parse(JSON.stringify(this.process));
      process.tasks.forEach((task) => {
        if (task.name == 'PageBuilder' && task?.props?.jsonContent?.children) {
          this.resetPageChartData(task.props.jsonContent.children);
        }
      });

      return process;
    },
    async saveFlow() {
      if (!this.isUserHasAccess || this.loading) return;
      if (!this.process?.name || this.process.name.trim() === '') {
        this.$emit(
          'showToaster',
          'Bitte geben Sie den Namen des Workflows ein',
          'Fehler',
          'danger'
        );
        return;
      }
      this.loading = true;
      try {
        const res = await this.$store.dispatch('saveFlow', {
          flow: this.getFilterProcessData(),
        });
        if (res && typeof res == 'number') {
          this.$emit('showToaster', 'Gespeichert !', 'Erfolgreich', 'success');
        } else {
          throw new Error(res);
        }
      } catch (error) {
        this.$emit(
          'showToaster',
          'Fehler beim Speichern !',
          'Fehlerhaft !',
          'danger'
        );
      } finally {
        this.loading = false;
      }
    },
    toggleSetting(isVisible = true) {
      this.isSettingsModalVisible = isVisible;
    },

    async showPagePreview() {
      try {
        this.isPreviewLoading = true;

        const payload = {
          content: this.getPagePayloadContent(),
          values: this.getAPIPayloadInputs,
          filename: this.task.props.reportName,
          htmlOnly: true,
        };
        const res = await generatePdf(payload);
        const blob = new Blob([res.data.data], { type: 'text/html' });
        this.pagePreviewBlobURL = URL.createObjectURL(blob);
      } catch (error) {
        this.$emit('showToaster', 'Vorschau nicht möglich', 'Fehler', 'danger');
      } finally {
        this.isPreviewLoading = false;
      }
    },
    toggleOffPreview() {
      this.isPreviewLoading = false;
      this.revokePageURL();
    },
    revokePageURL() {
      if (this.pagePreviewBlobURL) {
        URL.revokeObjectURL(this.pagePreviewBlobURL);
        this.pagePreviewBlobURL = null;
      }
    },
    updatePageNodeValues(pageChildren, name, value) {
      pageChildren.forEach((pageNode) => {
        if (
          pageChartTypes.includes(pageNode.type) &&
          pageNode.inputDataInfo.name == name
        ) {
          pageNode.value[getPagePropChartKey(pageNode.type)] = value;
        }
        if (pageNode.type == 'col') {
          pageNode.elements.forEach((element) => {
            this.updatePageNodeValues(element.children, name, value);
          });
        }
      });
    },

    async addChartDataInPageNode() {
      try {
        this.loading = true;
        this.task.input.forEach((input) => {
          if (pageChartTypes.includes(input.type)) {
            this.updatePageNodeValues(
              this.task.props.jsonContent.children,
              input.name,
              input.value
            );
          }
        });
      } catch (error) {
        this.$emit(
          'showToaster',
          'Diagrammdaten können nicht festgelegt werden',
          'Fehler',
          'danger'
        );
      } finally {
        setTimeout(() => {
          this.loading = false;
        });
      }
    },
  },

  components: {
    PageBuilderComponent,
    LoadingSpinner,
    PagePreviewModal,
    PagebuilderSettingsModal,
  },
};
</script>

<style scoped>
.page-builder-wrapper {
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  background-color: rgba(0, 0, 0, 0.1);
  z-index: 999;
  display: flex;
  align-items: center;
  justify-content: center;
}

.page-builder-wrapper .modal.task {
  padding-inline: 0px !important;
  padding-bottom: 50px;
}

.page-builder-wrapper .modal.task .custom-page-wrapper {
  padding-left: 34px !important;
}

.content {
  background-color: #fff;
  width: 100%;
  -webkit-box-sizing: border-box;
  box-sizing: border-box;
  text-align: center;
  height: calc(100% - 36px - 17px);
  margin-top: 17px;
  padding-right: 34px !important;
}

.pull-right {
  float: right;
  margin-right: 5px;
  margin-top: 5px;
}
::v-deep .column-selector,
::v-deep .row-selector {
  box-shadow: unset;
  padding: 0;
}

::v-deep input {
  box-sizing: border-box;
}
::v-deep .codelos-page-content {
  text-align: left;
  font-size: 16px;
  color: #212529;
}

.codelos-page-content * {
  box-sizing: border-box !important;
}

::v-deep .codelos-hr-block .element-wrapper .draggible-icon svg {
  top: 1px;
}
::v-deep .codelos-text-block .element-wrapper .draggible-icon svg {
  top: 11px;
}
::v-deep .element-block {
  line-height: 1.5;
}

::v-deep .modal-backdrop.custom-backdrop {
  background: transparent;
}
::v-deep .codelos-dropdown-child,
::v-deep .codelos-dropdown {
  box-sizing: border-box;
}
::v-deep .codelos-dropdown-item {
  line-height: 1.5;
}

::v-deep .codelos-table-block .table-wrap tr td input {
  font-size: 16px;
  color: #212529;
}
::v-deep .codelos-table-block .codelos-table-header td input {
  font-weight: 600;
  font-size: 18px;
  color: #212529;
}
::v-deep .codelos-table-block .table-centered .table-wrap table {
  box-sizing: border-box;
}
::v-deep .codelos-text-block .element-wrapper .draggible-icon {
  top: -12px;
}
.btn-close {
  position: relative;
  z-index: 9;
  top: 0;
  right: 0;
}

.loading.transparent {
  background-color: #9191912e;
}
.progress-icon-wrapper {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 100%;
  text-align: center;
}

.progress-icon-wrapper .progress-icon {
  left: unset;
  margin: 0 auto;
  top: unset;
  margin-top: 30px;
}
.progress-icon-wrapper .progress-icon:after {
  border-color: #313745 transparent #313745 transparent;
}
.custom-page-wrapper {
  max-width: 100%;
  padding: 0;
  display: flex;
  flex-direction: row-reverse;
  justify-content: space-between;
  padding: 0 0;
  align-items: center;
}
.custom-page-header input {
  font-size: 15px !important;
  line-height: 28px !important;
  cursor: text !important;
  width: 100% !important;
  max-width: 100% !important;
  margin: 0 !important;
  color: #000 !important;
  border: 1px solid #e2e2e2 !important;
  border-radius: 6px !important;
  padding: 12px 12px !important;
  height: 38px !important;
}

.custom-page-header input:focus {
  outline: none;
  box-shadow: none;
}

.custom-page-header input::placeholder {
  color: #8b8b8b;
}
.report-tools {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
}

.report-tools .round-button {
  width: 42px;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 100%;
  font-size: 18px;
  color: #2c3e50;
  cursor: pointer;
  border: 1px solid #d2d2d2;
}

.is-duplicate {
  background: #ff050578;
}

.error {
  animation: shake 0.2s ease-in-out 0s 2;
  border: none;
  box-shadow: 0 0 0.5em red;
}

@keyframes shake {
  0% {
    margin-left: 2rem;
  }
  25% {
    margin-left: 2.2rem;
  }
  75% {
    margin-left: 1.8rem;
  }
  100% {
    margin-left: 2rem;
  }
}
.modal.task .page-builder-close-btn {
  position: absolute;
  right: 10px;
  top: 10px;
}

.modal.task .page-builder-close-btn .btn-close {
  margin-left: auto;
}
.modal.task .page-builder-outer-wrapper {
  height: calc(100% - 75px);
  overflow: auto;
  margin-top: 35px;
}

.page-builder-wrapper .modal-backdrop {
  z-index: 998;
}

.page-builder-wrapper .modal {
  z-index: 999;
}

.page-builder-wrapper
  .content
  .page-builder-outer-wrapper
  ::v-deep
  .codelos-page-content {
  padding-top: 0 !important;
  padding-bottom: 10px !important;
}

.page-builder-wrapper .content .loading-container {
  position: absolute;
  height: 100%;
  width: 100%;
  top: 0;
  left: 0;
  z-index: 11;
}

::v-deep .element-wrapper .add-element-icon {
  padding-top: 1px !important;
}

.page-builder-wrapper .settings-modal-wrapper {
  position: absolute;
  right: 0;
  top: 0;
  z-index: 20;
  height: 100%;
}
.page-builder-wrapper .preview-wrapper {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  background: #ffffff;
  z-index: 12;
}
/* .page-builder-wrapper .preview-iframe {
  height: 100%;
  width: 100%;
} */

::v-deep .codelos-html-wrapper {
  min-height: 50px;
  /* border: 1px solid red; */
}
</style>
